/**
 * User ID Utilities
 * 
 * Provides reliable user ID extraction from various sources.
 * 
 * Priority order:
 * 1. JWT token (id_token or access_token) - Most reliable
 * 2. localStorage userId - Fallback
 * 3. null - Not authenticated
 */

import { parseJwtPayload } from './jwtUtils';
import { createFileLogger } from './logger';

const log = createFileLogger('USER_ID_UTILS');

/**
 * Get current user ID from various sources (most reliable first)
 * 
 * This ensures consistent user ID across the app and prevents
 * cache key mismatches due to timing issues with userTrackingService.
 * 
 * @returns User ID (Auth0 sub) or null if not authenticated
 */
export const getCurrentUserId = (): string | null => {
  // Try JWT tokens first (most reliable - always present when authenticated)
  const idToken = localStorage.getItem('id_token');
  const accessToken = localStorage.getItem('authToken');
  const token = idToken || accessToken;
  
  if (token) {
    try {
      const payload = parseJwtPayload(token);
      if (payload?.sub) {
        return payload.sub;
      }
    } catch (error) {
      log.warn('[getCurrentUserId] Failed to parse JWT token:', error);
    }
  }
  
  // Fallback to localStorage userId (set by userTrackingService)
  const userId = localStorage.getItem('userId');
  if (userId && userId !== 'unknown') {
    return userId;
  }
  
  // Not authenticated or invalid token
  return null;
};

/**
 * Check if user is authenticated (has valid user ID)
 * 
 * @returns true if user has valid authentication, false otherwise
 */
export const hasValidUserId = (): boolean => {
  return getCurrentUserId() !== null;
};


